import { SearchOutlined } from '@ant-design/icons';
import { Input, InputRef } from 'antd';
import { ColorButtonComponent } from 'components';
import React, { useRef } from 'react';
import { SetURLSearchParams } from 'react-router-dom';
import { TableActionInterface, TableOptionInterface } from 'type/tableType';
import './TableHeader.css';

interface TableHeaderProps<T> {
  name: string;
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  options?: TableOptionInterface;
  actions?: TableActionInterface<T>;
  additionalButton?: JSX.Element[];
}

const getSearchTermComponent = (
  inputRef: React.RefObject<InputRef>,
  searchParams: URLSearchParams,
  setSearchParams: SetURLSearchParams
): JSX.Element => {
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div className="search-input-wrapper">
          <Input
            ref={inputRef}
            className="search-input"
            placeholder="Search"
            prefix={<SearchOutlined />}
            defaultValue={searchParams.get('search') || ''}
            onKeyUp={e => {
              if (e.key === 'Enter') {
                setSearchParams(prev => {
                  prev.set('search', (e.target as HTMLInputElement).value);
                  prev.set('page', '1');
                  return prev;
                });
              }
            }}
          />
        </div>
        <ColorButtonComponent
          icon={<SearchOutlined />}
          text="Search"
          size="small"
          onClick={() => {
            setSearchParams(prev => {
              prev.set('search', inputRef.current?.input?.value || '');
              prev.set('page', '1');
              return prev;
            });
          }}
        />
      </div>
    </>
  );
};

const getAddButtonComponent = (handleAddClick: () => void, name: string): JSX.Element => {
  return <ColorButtonComponent text={`+ Add ${name}`} onClick={handleAddClick} size={'small'} />;
};

const getBulkDeleteButtonComponent = (
  searchParams: URLSearchParams,
  handleBulkDelete: (ids: number[]) => Promise<void>
): JSX.Element => {
  return (
    <ColorButtonComponent
      color="whte"
      text="Bulk Delete"
      onClick={() => handleBulkDelete(searchParams.get('ids')?.split(',').map(Number) || [])}
      size={'small'}
    />
  );
};

const TableHeader = <T,>({
  name,
  searchParams,
  setSearchParams,
  options = { isSearchable: true },
  actions,
  additionalButton,
}: TableHeaderProps<T>): JSX.Element => {
  const inputRef = useRef<InputRef>(null);

  return (
    <>
      <div className="table-name">
        <h1>{name}</h1>
      </div>
      <div className="table-header">
        <div className="table-header-left">
          {options?.isSearchable && getSearchTermComponent(inputRef, searchParams, setSearchParams)}
        </div>
        <div className="table-header-right">
          {actions?.onAddClick && getAddButtonComponent(actions.onAddClick, name)}
          {additionalButton?.map((button, index) => (
            <React.Fragment key={`additional-button-${index}`}>{button}</React.Fragment>
          ))}
          <div className="bulk-delete-button-wrapper">
            {actions?.onBulkDelete &&
              getBulkDeleteButtonComponent(searchParams, actions.onBulkDelete)}
          </div>
        </div>
      </div>
    </>
  );
};

export default TableHeader;
